import { useEffect, useState } from "react";
import { FileBarChart, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PageHeader, Empty } from "@/components/app/ui";
import { SectionHelp } from "@/components/app/Help";
import { api, errMsg, can, downloadFile, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  ["executive", "Executive Report", "Compliance posture, high-risk findings, open remediation, regulatory readiness, key recommendations."],
  ["privacy", "Privacy Report", "Control-level assessment, regulatory references, evidence, findings and remediation."],
  ["technical", "Technical Report", "Security controls, data systems, access controls, discovery findings and technical remediation."],
  ["audit", "Audit Evidence Report", "Requirement → Control → Evidence → Assessment → Finding → Remediation → Closure."],
  ["board", "Board Change Summary", "One page: improvements and new or worsened gaps since the previous cycle, current posture and days to 13 May 2027."],
];

export default function Reports() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/reports").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const gen = async (k) => {
    setBusy(k);
    try { const { data } = await api.post(`/reports/${k}`); toast.success(`${data.title} generated`); load(); } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  return (
    <div>
      <PageHeader title="Reports" subtitle="PDF reports record assessment version, regulatory version, evidence snapshot, generation date and author." />
      <SectionHelp id="reports" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {TYPES.map(([k, t, desc]) => (
          <div key={k} className="flex flex-col rounded-lg border border-slate-200 bg-white p-5" data-testid={`report-card-${k}`}>
            <FileBarChart className="h-5 w-5 text-blue-600" />
            <div className="mt-3 font-display font-semibold text-slate-900">{t}</div>
            <p className="mt-1 flex-1 text-xs leading-relaxed text-slate-500">{desc}</p>
            {can(user, "report") ? <Button onClick={() => gen(k)} disabled={!!busy} className="mt-4 bg-blue-600 hover:bg-blue-700" data-testid={`generate-report-${k}`}>{busy === k ? <Loader2 className="h-4 w-4 animate-spin" /> : "Generate PDF"}</Button>
              : <p className="mt-4 text-xs text-slate-400">Your role can download previously generated reports.</p>}
          </div>
        ))}
      </div>
      <h2 className="mb-3 mt-8 font-display text-lg font-bold">Generated reports</h2>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="reports-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500"><tr>{["Report", "Assessment version", "Regulatory version", "Evidence version", "Generated", ""].map((h) => <th key={h} className="px-4 py-2.5">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-t" data-testid={`report-row-${r.id}`}>
              <td className="px-4 py-2.5"><div className="font-medium">{r.title}</div><div className="font-mono text-[10px] text-slate-400">{r.id}</div></td>
              <td className="px-4 py-2.5 text-xs">{r.assessment_id} v{r.assessment_version}</td>
              <td className="px-4 py-2.5 text-xs">{r.regulatory_versions}</td>
              <td className="px-4 py-2.5 font-mono text-xs">{r.evidence_version}</td>
              <td className="px-4 py-2.5 text-xs">{fmtDateTime(r.generated_at)}<div className="text-slate-500">{r.generated_by}</div></td>
              <td className="px-4 py-2.5"><Button size="sm" variant="outline" onClick={() => downloadFile(`/reports/${r.id}/download`, `${r.type}-report.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`download-report-${r.id}`}><Download className="h-4 w-4" /></Button></td>
            </tr>))}</tbody>
        </table>
        {!rows.length && <Empty text="No reports generated yet." />}
      </div>
    </div>
  );
}
